import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from "recharts";

import { Card, CardContent } from "@/shared/ui/card";

import type { GeoReport } from "../../../api/geoService";

const DIMS: { key: keyof GeoReport["dimensions"]; label: string; weight: string }[] = [
  { key: "visibility", label: "可见度", weight: "40%" },
  { key: "rank", label: "推荐排位", weight: "25%" },
  { key: "breadth", label: "平台广度", weight: "15%" },
  { key: "sentiment", label: "情感口碑", weight: "10%" },
  { key: "citation", label: "引用源", weight: "10%" },
];

/** 每维一句人话(2026-07-11 用户反馈:分数要讲得给老板听);probes 缺失(旧报告)返 null 隐藏。 */
function dimNote(report: GeoReport, key: keyof GeoReport["dimensions"]): string | null {
  const rec = (report.probes ?? []).filter((p) => p.stage !== "doubt");
  if (rec.length === 0) return null;
  const mentioned = rec.filter((p) => p.mentioned);
  if (key === "visibility") {
    const pos = mentioned.filter((p) => p.sentiment === "positive").length;
    return `${rec.length} 次提问中 ${pos} 次被正面提及`;
  }
  if (key === "rank") {
    const ranks = mentioned.map((p) => p.rank).filter((r): r is number => typeof r === "number");
    if (ranks.length === 0) return "被提及时未进推荐名单";
    return `被提及时平均排第 ${(ranks.reduce((a, b) => a + b, 0) / ranks.length).toFixed(1)} 位`;
  }
  if (key === "breadth") {
    const all = new Set(rec.map((p) => p.platform)).size;
    const withMention = new Set(mentioned.map((p) => p.platform)).size;
    return `${all} 个引擎里 ${withMention} 个提到过你`;
  }
  if (key === "sentiment") {
    const pos = mentioned.filter((p) => p.sentiment === "positive").length;
    const neg = mentioned.filter((p) => p.sentiment === "negative").length;
    return `正面 ${pos} 条 · 负面 ${neg} 条`;
  }
  const n = report.citation_sources?.length ?? 0;
  return n > 0 ? `回溯到 ${n} 个引用域名` : "本轮未回溯到引用域名";
}

/** 五维能力雷达:真雷达图(recharts)+ 分维进度条(带权重,评分可解释)。 */
export function DimensionRadar({ report }: { report: GeoReport }) {
  const data = DIMS.map((d) => ({
    dim: d.label,
    score: Math.min(100, Math.max(0, report.dimensions[d.key])),
  }));
  return (
    <Card>
      <CardContent className="p-6">
        <div className="text-sm font-medium">五维能力雷达</div>
        <div className="mt-2 flex flex-col items-center gap-4 md:flex-row">
          <div className="h-56 w-full max-w-[280px]" data-testid="geo-radar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={data} outerRadius="72%">
                <PolarGrid stroke="hsl(var(--chart-grid))" />
                <PolarAngleAxis
                  dataKey="dim"
                  tick={{ fill: "hsl(var(--chart-axis))", fontSize: 12 }}
                />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar
                  dataKey="score"
                  stroke="hsl(var(--chart-1))"
                  fill="hsl(var(--chart-1))"
                  fillOpacity={0.28}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full flex-1 space-y-2.5">
            {DIMS.map((d) => {
              const note = dimNote(report, d.key);
              return (
                <div key={d.key}>
                  <div className="flex items-center gap-3">
                    <div className="w-16 shrink-0 text-sm text-muted-foreground">{d.label}</div>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{
                          width: `${Math.min(100, Math.max(0, report.dimensions[d.key]))}%`,
                        }}
                      />
                    </div>
                    <div className="w-24 shrink-0 text-right text-sm tabular-nums">
                      <span className="font-medium">{Math.round(report.dimensions[d.key])} 分</span>
                      <span className="text-xs text-muted-foreground">(占{d.weight})</span>
                    </div>
                  </div>
                  {note && (
                    <div className="ml-[76px] mt-0.5 text-xs text-muted-foreground">{note}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
